import { useEffect, useMemo, useState } from "react";
import {
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  type ProviderInstanceId,
  type ThreadId,
} from "@t3tools/contracts";
import {
  appendProviderSessionImportPage,
  filterAndSortProviderSessionImportItems,
  providerSessionImportAction,
  providerSessionImportCanSubmit,
  providerSessionImportErrorReason,
  providerSessionImportNeedsActivityAcknowledgement,
  type ProviderSessionImportItem,
  type ProviderSessionImportTimeRange,
} from "@t3tools/client-runtime/operations/provider-session-import";
import type { EnvironmentProject } from "@t3tools/client-runtime/state/models";
import { scopeThreadRef } from "@t3tools/client-runtime/environment";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";
import { CheckCircle2Icon, InfoIcon, RefreshCwIcon, SearchIcon, XIcon } from "lucide-react";

import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogDescription, DialogFooter, DialogPopup, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "./ui/select";
import { ProviderInstanceIcon } from "./chat/ProviderInstanceIcon";
import { orchestrationEnvironment } from "../state/orchestration";
import { useServerConfigs } from "../state/entities";
import { useEnvironmentQuery } from "../state/query";
import { useAtomCommand } from "../state/use-atom-command";
import { environmentThreadShells } from "../state/threads";
import { appAtomRegistry } from "../rpc/atomRegistry";
import { deriveProviderInstanceEntries } from "../providerInstances";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { i18n } from "../i18n";
import { cn, newThreadId } from "../lib/utils";

type ImportProviderEntry = ReturnType<typeof deriveProviderInstanceEntries>[number];

function importErrorLabel(error: unknown): string {
  const reason = providerSessionImportErrorReason(error);
  return reason === null
    ? i18n.t("providerImport.failed")
    : i18n.t(`providerImport.error.${reason}`);
}

function availabilityPresentation(availability: ProviderSessionImportItem["availability"]): {
  readonly label: string;
  readonly className: string;
} {
  switch (availability) {
    case "available":
      return {
        label: i18n.t("providerImport.status.available"),
        className: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
      };
    case "busy":
      return {
        label: i18n.t("providerImport.status.busy"),
        className: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
      };
    case "activity-unknown":
      return {
        label: i18n.t("providerImport.status.activityUnknown"),
        className: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
      };
    case "already-imported":
      return {
        label: i18n.t("providerImport.status.alreadyImported"),
        className: "bg-muted text-muted-foreground",
      };
    case "archived":
      return {
        label: i18n.t("providerImport.status.archived"),
        className: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
      };
    case "deleting":
      return {
        label: i18n.t("providerImport.status.deleting"),
        className: "bg-muted text-muted-foreground",
      };
  }
}

function defaultModel(entry: ImportProviderEntry): string | null {
  return entry.models.find((model) => model.isDefault)?.slug ?? entry.models[0]?.slug ?? null;
}

async function waitForImportedThreadShell(
  environmentId: EnvironmentProject["environmentId"],
  threadId: ThreadId,
): Promise<void> {
  const atom = environmentThreadShells.threadShellAtom(scopeThreadRef(environmentId, threadId));
  if (appAtomRegistry.get(atom) !== null) return;
  await new Promise<void>((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      unsubscribe();
      resolve();
    };
    const unsubscribe = appAtomRegistry.subscribe(atom, (thread) => {
      if (thread === null) return;
      finish();
    });
    if (appAtomRegistry.get(atom) !== null) finish();
  });
}

export function ProviderSessionImportDialog(props: {
  readonly open: boolean;
  readonly project: EnvironmentProject | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onNavigateToThread: (threadId: ThreadId) => void;
}) {
  const serverConfigs = useServerConfigs();
  const providers = props.project
    ? (serverConfigs.get(props.project.environmentId)?.providers ?? [])
    : [];
  const providerEntries = useMemo(
    () =>
      deriveProviderInstanceEntries(providers).filter(
        (entry) => entry.enabled && entry.snapshot.supportsSessionImport,
      ),
    [providers],
  );
  const [selectedProviderInstanceId, setSelectedProviderInstanceId] =
    useState<ProviderInstanceId | null>(null);
  const selectedProvider =
    providerEntries.find((entry) => entry.instanceId === selectedProviderInstanceId) ??
    providerEntries.find((entry) => entry.snapshot.supportsSessionImport) ??
    providerEntries[0] ??
    null;
  const canQuery =
    props.open &&
    props.project !== null &&
    selectedProvider !== null &&
    selectedProvider.snapshot.supportsSessionImport;
  const targetKey = canQuery
    ? `${props.project!.environmentId}:${props.project!.id}:${selectedProvider!.instanceId}`
    : null;
  const [pageState, setPageState] = useState<{
    readonly targetKey: string | null;
    readonly requestedCursor: string | undefined;
    readonly items: ReadonlyArray<ProviderSessionImportItem>;
    readonly nextCursor: string | undefined;
  }>({ targetKey: null, requestedCursor: undefined, items: [], nextCursor: undefined });
  const activePageState =
    pageState.targetKey === targetKey
      ? pageState
      : { targetKey, requestedCursor: undefined, items: [] as const, nextCursor: undefined };
  const requestedCursor = activePageState.requestedCursor;
  const sessions = useEnvironmentQuery(
    canQuery
      ? orchestrationEnvironment.importableProviderSessions({
          environmentId: props.project!.environmentId,
          input: {
            projectId: props.project!.id,
            providerInstanceId: selectedProvider!.instanceId,
            ...(requestedCursor === undefined ? {} : { cursor: requestedCursor }),
            limit: 50,
          },
        })
      : null,
  );
  const [query, setQuery] = useState("");
  const [timeRange, setTimeRange] = useState<ProviderSessionImportTimeRange>("all");
  const [filterNow, setFilterNow] = useState(() => Date.now());
  const [selectedCandidateRef, setSelectedCandidateRef] = useState<string | null>(null);
  const [activityAcknowledged, setActivityAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  useEffect(() => {
    if (targetKey === null || sessions.data === null) return;
    setPageState((current) => {
      const base =
        current.targetKey === targetKey && current.requestedCursor === requestedCursor
          ? current
          : {
              targetKey,
              requestedCursor,
              items: [] as ReadonlyArray<ProviderSessionImportItem>,
              nextCursor: undefined,
            };
      const appended = appendProviderSessionImportPage({
        current: {
          items: base.items,
          ...(base.nextCursor === undefined ? {} : { nextCursor: base.nextCursor }),
        },
        ...(requestedCursor === undefined ? {} : { requestedCursor }),
        page: sessions.data!,
      });
      return {
        targetKey,
        requestedCursor,
        items: appended.items,
        nextCursor: appended.nextCursor,
      };
    });
  }, [requestedCursor, sessions.data, targetKey]);
  const loadedItems = activePageState.items;
  const items = useMemo(
    () =>
      filterAndSortProviderSessionImportItems({
        items: loadedItems,
        query,
        sortOrder: "recent",
        timeRange,
        now: filterNow,
      }),
    [filterNow, loadedItems, query, timeRange],
  );
  const selectedItem =
    loadedItems.find((item) => item.candidateRef === selectedCandidateRef) ?? null;
  const importSession = useAtomCommand(orchestrationEnvironment.importProviderSession, {
    reportFailure: false,
  });

  const selectProvider = (instanceId: ProviderInstanceId) => {
    setSelectedProviderInstanceId(instanceId);
    setSelectedCandidateRef(null);
    setActivityAcknowledged(false);
    setImportError(null);
    setFilterNow(Date.now());
  };
  const selectItem = (item: ProviderSessionImportItem) => {
    const action = providerSessionImportAction(item);
    if (action === "disabled") return;
    if (action === "navigate" && item.existingThreadId) {
      props.onOpenChange(false);
      props.onNavigateToThread(item.existingThreadId);
      return;
    }
    setSelectedCandidateRef(item.candidateRef);
    setActivityAcknowledged(false);
    setImportError(null);
  };
  const refreshSessions = () => {
    setSelectedCandidateRef(null);
    setActivityAcknowledged(false);
    setImportError(null);
    setPageState({
      targetKey,
      requestedCursor: undefined,
      items: [],
      nextCursor: undefined,
    });
    sessions.refresh();
  };
  const submit = async () => {
    if (!props.project || !selectedProvider || !selectedItem) return;
    const model = defaultModel(selectedProvider);
    if (!model) {
      setImportError(i18n.t("providerImport.noModel"));
      return;
    }
    setSubmitting(true);
    setImportError(null);
    const result = await importSession({
      environmentId: props.project.environmentId,
      input: {
        projectId: props.project.id,
        providerInstanceId: selectedProvider.instanceId,
        candidateRef: selectedItem.candidateRef,
        requestedThreadId: newThreadId(),
        modelSelection: { instanceId: selectedProvider.instanceId, model },
        runtimeMode: DEFAULT_RUNTIME_MODE,
        interactionMode: DEFAULT_PROVIDER_INTERACTION_MODE,
        createdAt: new Date().toISOString(),
        ...(providerSessionImportNeedsActivityAcknowledgement(selectedItem) && activityAcknowledged
          ? { activityAcknowledgement: "confirmed-stopped" as const }
          : {}),
      },
    });
    if (result._tag === "Failure") {
      setSubmitting(false);
      if (!isAtomCommandInterrupted(result)) {
        setImportError(importErrorLabel(squashAtomCommandFailure(result)));
      }
      return;
    }
    await waitForImportedThreadShell(props.project.environmentId, result.value.threadId);
    props.onOpenChange(false);
    props.onNavigateToThread(result.value.threadId);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup
        showCloseButton={false}
        className="flex h-[min(480px,calc(100dvh-2rem))] w-[min(660px,calc(100vw-2rem))] max-w-none flex-col overflow-hidden"
      >
        <DialogTitle className="sr-only">{i18n.t("providerImport.title")}</DialogTitle>
        <DialogDescription className="sr-only">
          {props.project
            ? i18n.t("providerImport.description", { project: props.project.title })
            : i18n.t("providerImport.projectUnavailable")}
        </DialogDescription>
        <div className="min-h-0 flex-1 overflow-hidden">
          <div className="grid h-full min-h-0 grid-cols-[156px_minmax(0,1fr)] max-sm:grid-cols-1">
            <aside className="border-r bg-muted/20 p-4 max-sm:border-b max-sm:border-r-0">
              <div className="mb-3 px-1 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
                {i18n.t("providerImport.providers")}
              </div>
              <div className="grid gap-1">
                {providerEntries.map((entry) => {
                  const active = selectedProvider?.instanceId === entry.instanceId;
                  return (
                    <button
                      key={entry.instanceId}
                      type="button"
                      onClick={() => selectProvider(entry.instanceId)}
                      className={cn(
                        "flex min-h-11 w-full items-center gap-2.5 rounded-lg border px-2.5 text-left transition-colors",
                        active
                          ? "border-primary/50 bg-primary/8 text-foreground"
                          : "border-transparent text-muted-foreground hover:bg-accent hover:text-foreground",
                      )}
                    >
                      <ProviderInstanceIcon
                        driverKind={entry.driverKind}
                        displayName={entry.displayName}
                        accentColor={entry.accentColor}
                        iconClassName="size-5"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">
                        {entry.displayName}
                      </span>
                    </button>
                  );
                })}
                {providerEntries.length === 0 ? (
                  <div className="px-2 py-6 text-sm text-muted-foreground">
                    {i18n.t("providerImport.noProviders")}
                  </div>
                ) : null}
              </div>
            </aside>
            <section className="flex min-h-0 min-w-0 flex-col p-4">
              {selectedProvider ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative min-w-0 flex-1">
                      <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        value={query}
                        onChange={(event) => setQuery(event.currentTarget.value)}
                        placeholder={i18n.t("providerImport.search")}
                        className="pl-9"
                      />
                    </div>
                    <Select
                      value={timeRange}
                      onValueChange={(value) =>
                        setTimeRange(value as ProviderSessionImportTimeRange)
                      }
                    >
                      <SelectTrigger
                        className="w-24 min-w-0 shrink-0"
                        aria-label={i18n.t("providerImport.timeFilter")}
                      >
                        <SelectValue>{i18n.t(`providerImport.time.${timeRange}`)}</SelectValue>
                      </SelectTrigger>
                      <SelectPopup align="end" alignItemWithTrigger={false}>
                        {(["all", "7d", "30d", "90d"] as const).map((value) => (
                          <SelectItem key={value} value={value}>
                            {i18n.t(`providerImport.time.${value}`)}
                          </SelectItem>
                        ))}
                      </SelectPopup>
                    </Select>
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={i18n.t("providerImport.refresh")}
                      title={i18n.t("providerImport.refresh")}
                      onClick={refreshSessions}
                    >
                      <RefreshCwIcon className="size-4" />
                    </Button>
                    <div className="h-5 w-px shrink-0 bg-border" />
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      aria-label={i18n.t("common.close")}
                      title={i18n.t("common.close")}
                      onClick={() => props.onOpenChange(false)}
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </div>
                  <div className="mt-2.5 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto pr-2 [scrollbar-color:color-mix(in_oklab,var(--muted-foreground)_28%,transparent)_transparent] [scrollbar-width:thin]">
                    {sessions.isPending && loadedItems.length === 0 ? (
                      <div className="grid min-h-48 place-items-center text-sm text-muted-foreground">
                        {i18n.t("providerImport.loading")}
                      </div>
                    ) : sessions.error && loadedItems.length === 0 ? (
                      <div className="grid min-h-48 place-items-center gap-3 p-6 text-center text-sm text-muted-foreground">
                        <span>{importErrorLabel(sessions.failure)}</span>
                        <Button size="sm" variant="outline" onClick={sessions.refresh}>
                          {i18n.t("common.tryAgain")}
                        </Button>
                      </div>
                    ) : items.length === 0 ? (
                      <div className="grid min-h-48 place-items-center p-6 text-center text-sm text-muted-foreground">
                        {query.trim().length > 0
                          ? i18n.t("providerImport.noMatches")
                          : i18n.t("providerImport.empty")}
                      </div>
                    ) : (
                      items.map((item) => {
                        const status = availabilityPresentation(item.availability);
                        const selected = selectedItem?.candidateRef === item.candidateRef;
                        const disabled = providerSessionImportAction(item) === "disabled";
                        return (
                          <button
                            key={item.candidateRef}
                            type="button"
                            disabled={disabled}
                            onClick={() => selectItem(item)}
                            className={cn(
                              "flex min-h-14 items-center gap-3 rounded-lg border border-transparent bg-muted/28 px-4 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
                              selected
                                ? "border-primary/35 bg-primary/8 shadow-sm"
                                : "hover:border-border/70 hover:bg-accent/55",
                            )}
                          >
                            <span
                              className={cn(
                                "grid size-4 shrink-0 place-items-center rounded-full border",
                                selected && "border-primary bg-primary text-primary-foreground",
                              )}
                            >
                              {selected ? <CheckCircle2Icon className="size-3.5" /> : null}
                            </span>
                            <span className="min-w-0 flex-1 truncate text-sm font-medium">
                              {item.title}
                            </span>
                            <span className="shrink-0 text-xs text-muted-foreground">
                              {formatRelativeTimeLabel(item.lastActivityAt)}
                            </span>
                            <span
                              className={cn(
                                "shrink-0 rounded-full px-2 py-1 text-xs",
                                status.className,
                              )}
                            >
                              {status.label}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {activePageState.nextCursor !== undefined ||
                  (sessions.isPending && loadedItems.length > 0) ||
                  (sessions.error !== null && loadedItems.length > 0) ? (
                    <div className="mt-3 flex justify-center">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={sessions.isPending}
                        onClick={() => {
                          if (sessions.error !== null) {
                            sessions.refresh();
                            return;
                          }
                          setPageState((current) =>
                            current.targetKey === targetKey && current.nextCursor !== undefined
                              ? {
                                  ...current,
                                  requestedCursor: current.nextCursor,
                                  nextCursor: undefined,
                                }
                              : current,
                          );
                        }}
                      >
                        {sessions.isPending
                          ? i18n.t("providerImport.loadingMore")
                          : sessions.error !== null
                            ? i18n.t("common.tryAgain")
                            : i18n.t("providerImport.loadMore")}
                      </Button>
                    </div>
                  ) : null}
                  {providerSessionImportNeedsActivityAcknowledgement(selectedItem) ? (
                    <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-amber-500/25 bg-amber-500/8 p-3 text-sm">
                      <Checkbox
                        checked={activityAcknowledged}
                        onCheckedChange={setActivityAcknowledged}
                        aria-label={i18n.t("providerImport.confirmStopped")}
                      />
                      <span className="leading-5 text-foreground/90">
                        {i18n.t("providerImport.confirmStopped")}
                      </span>
                    </label>
                  ) : null}
                  {importError ? (
                    <div className="mt-3 text-sm text-destructive-foreground">{importError}</div>
                  ) : null}
                </>
              ) : null}
            </section>
          </div>
        </div>
        <DialogFooter
          variant="bare"
          className="flex-row items-center gap-4 border-t bg-muted/25 px-5 py-3"
        >
          <div className="mr-auto flex min-w-0 items-start gap-2 text-xs text-muted-foreground">
            <InfoIcon className="mt-0.5 size-4 shrink-0" />
            <span className="line-clamp-3">{i18n.t("providerImport.directBindWarning")}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button variant="outline" onClick={() => props.onOpenChange(false)}>
              {i18n.t("common.cancel")}
            </Button>
            <Button
              onClick={() => void submit()}
              disabled={
                !providerSessionImportCanSubmit({
                  item: selectedItem,
                  activityAcknowledged,
                  submitting,
                })
              }
            >
              {submitting ? i18n.t("providerImport.importing") : i18n.t("providerImport.import")}
            </Button>
          </div>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}
